import React, { useState, useEffect } from 'react';
import api from '../services/api';

const CategoryFormModal = ({ category, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'cogs',
    is_vehicle_cost: false
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        type: category.categoryType || 'cogs',
        is_vehicle_cost: category.isVehicleCost || false
      });
    } else {
      setFormData({ name: '', description: '', type: 'cogs', is_vehicle_cost: false });
    }
  }, [category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="p-4 border-b border-neutral-200">
            <h3 className="font-medium">{category ? 'Edit Category' : 'New Category'}</h3>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Name *</label>
              <input type="text" value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded" required />
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Description</label>
              <input type="text" value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded" />
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Type *</label>
              <select value={formData.type} onChange={e => setFormData(f => ({ ...f, type: e.target.value }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded bg-white" required>
                <option value="cogs">COGS</option>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div className="flex items-center">
              <input type="checkbox" id="is_vehicle_cost" checked={formData.is_vehicle_cost} onChange={e => setFormData(f => ({ ...f, is_vehicle_cost: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <label htmlFor="is_vehicle_cost" className="ml-2 block text-sm text-gray-900">Is a direct vehicle cost?</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 bg-gray-50 border-t border-neutral-200">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-neutral-600 bg-white border rounded-md">Cancel</button>
            <button type="submit" className="px-4 py-2 text-sm bg-neutral-800 text-white rounded-md">{category ? 'Save Changes' : 'Create Category'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await api.getAccountingCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      alert('Could not load categories.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSave = async (formData) => {
    try {
      // Map form fields to API expected fields
      const apiData = {
        name: formData.name,
        categoryType: formData.type,
        isVehicleCost: formData.is_vehicle_cost,
        isSystem: false,
        sortOrder: 100
      };
      if (editingCategory) {
        await api.updateAccountingCategory(editingCategory.id, apiData);
      } else {
        await api.createAccountingCategory(apiData);
      }
      setShowForm(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Error saving category.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      // Soft delete via backend endpoint (sets active=false)
      await api.deleteAccountingCategory(id);
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Error deleting category.');
    }
  };

  const groupedCategories = categories.reduce((acc, cat) => {
    const type = cat.categoryType || 'uncategorized';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(cat);
    return acc;
  }, {});

  const categoryTypes = {
    'cogs': 'Cost of Goods Sold (COGS)',
    'expense': 'Expense',
    'income': 'Income'
  };

  if (loading) return <div>Loading categories...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Manage Categories</h1>
        <button onClick={() => { setEditingCategory(null); setShowForm(true); }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
          + New Category
        </button>
      </div>

      {Object.entries(categoryTypes).map(([type, title]) => (
        <div key={type} className="bg-white border border-neutral-200 rounded-lg">
          <h2 className="text-lg font-semibold p-3 border-b border-neutral-200">{title}</h2>
          {groupedCategories[type] && groupedCategories[type].length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Description</th>
                  <th className="p-3 font-medium">Vehicle Cost</th>
                  <th className="p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedCategories[type].map(c => (
                  <tr key={c.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="p-3">{c.name}</td>
                    <td className="p-3 text-xs text-neutral-600">{c.description}</td>
                    <td className="p-3">{c.isVehicleCost ? 'Yes' : 'No'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-4 text-xs">
                        <button onClick={() => { setEditingCategory(c); setShowForm(true); }} className="text-blue-600 hover:underline">Edit</button>
                        <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-neutral-500 italic p-3">No categories of this type.</p>
          )}
        </div>
      ))}

      {showForm && (
        <CategoryFormModal
          category={editingCategory}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
        />
      )}
    </div>
  );
}; 

export default CategoriesManager;
